import { useEffect, useState } from "react";
import { toast } from "react-toastify";
import axiosInstance from "../../axios/axiosInstance";
import { User, Zone } from "./types";

interface UserManagementPageProps {
  onSelectUsers: (users: User[]) => void;
  onClose: () => void;
}

export default function UserManagementPage({
  onSelectUsers,
  onClose,
}: UserManagementPageProps) {
  const [userFilters, setUserFilters] = useState({
    search: "",
    zone: "",
    area: "",
  });
  const [page, setPage] = useState(1);
  const [filteredUsers, setFilteredUsers] = useState<User[]>([]);
  const [checkedUsers, setCheckedUsers] = useState<Set<number>>(new Set());
  const [searchQuery, setSearchQuery] = useState("");
  const [allUsers, setAllUsers] = useState<User[]>([]);
  const [visibleUsers, setVisibleUsers] = useState<User[]>([]);
  const [zones, setZones] = useState<Zone[]>([])
  const rowsPerPage = 6;

  useEffect(() => {
    applyFilters();
  }, [userFilters]);

  const applyFilters = () => {
    let q = userFilters.search.trim().toLowerCase();
    const filtered = allUsers.filter((u) => {
      let matches = true;
      if (
        q &&
        !(
          u.name.toLowerCase().includes(q) ||
          u.address.toLowerCase().includes(q)
        )
      )
        matches = false;
      // if (userFilters.zone && u.zone !== userFilters.zone) matches = false;
      // if (userFilters.area && u.area !== userFilters.area) matches = false;
      return matches;
    });
    setFilteredUsers(filtered);
    if (page > Math.ceil(filtered.length / rowsPerPage)) setPage(1);
  };

  const handleCheckboxChange = (userId: number, checked: boolean) => {
    const newCheckedUsers = new Set(checkedUsers);
    if (checked) {
      newCheckedUsers.add(userId);
    } else {
      newCheckedUsers.delete(userId);
    }
    setCheckedUsers(newCheckedUsers);
  };

  const handleSelectAll = (checked: boolean) => {
    const visibleUsers = filteredUsers.slice(
      (page - 1) * rowsPerPage,
      page * rowsPerPage
    );
    const newCheckedUsers = new Set(checkedUsers);
    visibleUsers.forEach((u) => {
      if (checked) {
        newCheckedUsers.add(u.id);
      } else {
        newCheckedUsers.delete(u.id);
      }
    });
    setCheckedUsers(newCheckedUsers);
  };

  const handleAddSelected = () => {
    const selectedUsers = allUsers.filter((u) => checkedUsers.has(u.id));
    onSelectUsers(selectedUsers);
    onClose();
  };

  // const visibleUsers = filteredUsers.slice(
  //   (page - 1) * rowsPerPage,
  //   page * rowsPerPage
  // );

  const totalPages = Math.max(1, Math.ceil(filteredUsers.length / rowsPerPage));

  const handleGetUsers = async () => {
    // setLoading(true);
    try {
      const resp: any = await axiosInstance.get(
        `/api/user?search=${searchQuery}&page=${page}&limit=15`
      );
      if (resp.status === 200) {
        setAllUsers(resp.data.data.data || []);
      }
    } catch (error: any) {
      toast.error("Error getting users");
    }
  };

  useEffect(() => {
    setFilteredUsers(allUsers);
  }, [allUsers]);

  useEffect(() => {
    setVisibleUsers(
      (filteredUsers || []).slice((page - 1) * rowsPerPage, page * rowsPerPage)
    );
  }, [filteredUsers, page]);

  useEffect(() => {
    handleGetUsers();
  }, []);

  useEffect(() => {
    fetchZones();
  }, []);

  const fetchZones = async () => {
    // setLoading(true);
    try {
      const res = await axiosInstance.get<{ data: any[] }>(`/api/zone/all`);
      setZones(res.data?.data || []);
    } catch (err) {
      toast.error("Failed to fetch zones");
    } finally {
      // setLoading(false);
    }
  };
  return (

    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-40">
      <div className="bg-white rounded-2xl shadow-xl max-w-5xl w-full p-8 relative mt-8">
        {/* <div
        className="fixed inset-0 bg-black bg-opacity-50 transition-opacity"
        onClick={onClose}
      ></div> */}
        <div className="relative bg-neutral-50">
          <header className="border-b bg-white">
            <div className="container mx-auto px-4 py-4">
              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-4">
                  {/* <i className="fa-solid fa-cube text-2xl text-neutral-800 dark:text-white"></i> */}
                  {/* <span className="text-xl dark:text-white">FeatureHub</span> */}
                  Select Users
                </div>
                <button
                  onClick={onClose}
                  className="text-neutral-600 hover:text-neutral-800"
                >
                  <i className="fa-solid fa-xmark text-2xl"></i>
                </button>
              </div>
            </div>
          </header>

          <main className="container mx-auto px-4 py-10">
            <div className="mb-1">
              {/* <h1 className="text-3xl font-semibold mb-2 dark:text-white">
              User Management
            </h1>
            <p className="text-neutral-600 dark:text-neutral-400 text-lg">
              Select users to add to your notification.
            </p> */}
            </div>

            <div className="flex flex-wrap items-end gap-4 bg-white border rounded-2xl shadow px-6 py-5 mb-8">
              <div className="w-full md:w-64 relative">
                <input
                  type="text"
                  value={userFilters.search}
                  onChange={(e) =>
                    setUserFilters((prev) => ({
                      ...prev,
                      search: e.target.value,
                    }))
                  }
                  className="w-full rounded-lg border px-4 py-2.5 pr-10 focus:ring-2 focus:ring-neutral-800 focus:outline-none text-neutral-900 placeholder:text-neutral-400 text-base"
                  placeholder="Search users by name, email..."
                />
                <span className="absolute right-3 top-1/2 -translate-y-1/2 text-neutral-400">
                  <i className="fa-solid fa-magnifying-glass"></i>
                </span>
              </div>
              <div>
                <label className="text-neutral-800 text-sm font-medium mb-1 block">
                  Zone
                </label>
                <select
                  value={userFilters.zone}
                  onChange={(e) =>
                    setUserFilters((prev) => ({ ...prev, zone: e.target.value }))
                  }
                  className="rounded-lg border px-3 py-2 focus:ring-2 focus:ring-neutral-800 focus:outline-none text-neutral-900 text-base w-40 bg-white"
                >
                  <option value="0">All Zones</option>
                  {zones.map(z => <option value={z.id}>{z.zone_name}</option>)}
                </select>
              </div>
              {/* <div>
              <label className="text-neutral-800 text-sm font-medium mb-1 block">
                Area
              </label>
              <select
                value={userFilters.area}
                onChange={(e) =>
                  setUserFilters((prev) => ({ ...prev, area: e.target.value }))
                }
                className="rounded-lg border px-3 py-2 focus:ring-2 focus:ring-neutral-800 focus:outline-none text-neutral-900 text-base w-40 bg-white"
              >
                <option value="">All Areas</option>
                <option value="central">Central</option>
                <option value="uptown">Uptown</option>
                <option value="suburb">Suburb</option>
              </select>
            </div> */}
              <div className="flex-1"></div>
              <button
                onClick={handleAddSelected}
                disabled={checkedUsers.size === 0}
                className="flex items-center gap-2 bg-neutral-800 hover:bg-neutral-700 text-white px-4 py-2 rounded-lg font-medium shadow transition disabled:bg-neutral-300 disabled:text-neutral-600"
              >
                <i className="fa-solid fa-plus"></i>
                Add Selected Users
              </button>
            </div>

            <div className=" max-h-120 bg-white border rounded-2xl shadow-lg overflow-hidden">
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-neutral-200">
                  <thead className="bg-neutral-50">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-semibold text-neutral-500 tracking-wider w-12">
                        <input
                          type="checkbox"
                          checked={
                            visibleUsers.length > 0 &&
                            visibleUsers.every((u) => checkedUsers.has(u.id))
                          }
                          onChange={(e) => handleSelectAll(e.target.checked)}
                          className="accent-neutral-800 w-5 h-5 border-neutral-300 rounded"
                        />
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-semibold text-neutral-500 tracking-wider">
                        User
                      </th>
                      {/* <th className="px-6 py-3 text-left text-xs font-semibold text-neutral-500 tracking-wider">
                      Email
                    </th> */}
                      <th className="px-6 py-3 text-left text-xs font-semibold text-neutral-500 tracking-wider">
                        Phone
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-semibold text-neutral-500 tracking-wider">
                        Zone
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-semibold text-neutral-500 tracking-wider">
                        Address
                      </th>

                      <th className="px-6 py-3 text-left text-xs font-semibold text-neutral-500 tracking-wider">
                        Channels
                      </th>
                      {/* <th className="px-6 py-3 text-left text-xs font-semibold text-neutral-500 tracking-wider">
                      Area
                    </th> */}
                      <th className="px-6 py-3 text-left text-xs font-semibold text-neutral-500 tracking-wider">
                        Status
                      </th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-neutral-100 bg-white">
                    {visibleUsers.map((user) => {
                      const statusColor =
                        user.status === "Active"
                          ? "text-green-600 bg-green-50"
                          : user.status === "Pending"
                            ? "text-yellow-600 bg-yellow-50"
                            : "text-neutral-500 bg-neutral-100";
                      return (
                        <tr
                          key={user.id}
                          className="hover:bg-neutral-50 transition"
                        >
                          <td className="px-6 py-4">
                            <input
                              type="checkbox"
                              checked={checkedUsers.has(user.id)}
                              onChange={(e) =>
                                handleCheckboxChange(user.id, e.target.checked)
                              }
                              className="accent-neutral-800 w-5 h-5 rounded border-neutral-300"
                            />
                          </td>
                          {/* <td className="px-6 py-4">
                          <div className="flex items-center gap-3">
                            <img
                              src={user.avatar}
                              className="w-9 h-9 rounded-full border"
                              alt={user.name}
                            />
                            <span className="font-medium text-neutral-900 dark:text-white">
                              {user.name}
                            </span>
                          </div>
                        </td> */}
                          <td className="px-6 py-4">
                            <div className="flex items-center gap-3">
                              {/* <img
                              src={user.avatar}
                              className="w-9 h-9 rounded-full border"
                              alt={user.name}
                            /> */}
                              <span className="font-medium text-neutral-900">
                                {user.name}
                              </span>
                            </div>
                          </td>
                          {/* <td className="px-6 py-4 text-neutral-700 dark:text-neutral-300">
                          {user.email}
                        </td> */}
                          <td className="px-6 py-4 text-neutral-700">
                            {user.phone}
                          </td>
                          <td className="px-6 py-4">
                            <span className="px-2 py-1 bg-neutral-100 rounded text-xs text-neutral-700 capitalize">
                              {user.zone?.zone_name || "None  "}
                            </span>
                          </td>
                          <td className="px-6 py-4">
                            <span className="px-2 py-1 bg-neutral-100 rounded text-xs text-neutral-700 capitalize">
                              {user.address}
                            </span>
                          </td>
                          <td className="px-6 py-4">
                            <span title="SMS user" className="ml-2"><i className="fa-solid fa-sms text-blue-500"></i></span>
                            {user.is_whatsapp_enabled ? <span title="WhatsApp user" className="ml-1"><i className="fa-brands fa-whatsapp text-green-500"></i></span> : null}
                          </td>
                          <td className="px-6 py-4">
                            <span
                              className={`${statusColor} px-2 py-0.5 rounded text-xs font-semibold`}
                            >
                              {user.status}
                            </span>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
              {filteredUsers.length === 0 && (
                <div className="flex flex-col items-center justify-center py-20">
                  <img
                    className="w-28 h-28 mb-5"
                    src="https://storage.googleapis.com/uxpilot-auth.appspot.com/7f4fb021f1-f35dac0896ca43e0ce0d.png"
                    alt="No users found"
                  />
                  <span className="text-neutral-500 font-medium text-lg mb-2">
                    No users found
                  </span>
                  <span className="text-neutral-400">
                    Try adjusting your search or filter criteria.
                  </span>
                </div>
              )}
              <div className="flex flex-wrap items-center justify-between gap-4 px-6 py-4 border-t bg-neutral-50">
                <div>
                  <span className="text-sm text-neutral-500">
                    Showing{" "}
                    {filteredUsers.length === 0
                      ? 0
                      : (page - 1) * rowsPerPage + 1}
                    -{Math.min(page * rowsPerPage, filteredUsers.length)} of{" "}
                    {filteredUsers.length} users
                  </span>
                </div>
                <div className="flex gap-1">
                  <button
                    onClick={() => setPage((p) => Math.max(1, p - 1))}
                    disabled={page <= 1}
                    className="px-3 py-1 rounded-lg text-neutral-600 hover:bg-neutral-100 disabled:text-neutral-300"
                  >
                    <i className="fa-solid fa-angle-left"></i>
                  </button>
                  {Array.from({ length: totalPages }, (_, i) => i + 1).map(
                    (p) => (
                      <button
                        key={p}
                        onClick={() => setPage(p)}
                        className={`px-3 py-1 rounded-lg ${p === page
                          ? "bg-neutral-800 text-white"
                          : "text-neutral-600 hover:bg-neutral-100"
                          }`}
                      >
                        {p}
                      </button>
                    )
                  )}
                  <button
                    onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                    disabled={page >= totalPages}
                    className="px-3 py-1 rounded-lg text-neutral-600 hover:bg-neutral-100 disabled:text-neutral-300"
                  >
                    <i className="fa-solid fa-angle-right"></i>
                  </button>
                </div>
              </div>
            </div>
          </main>
        </div>
      </div>
    </div>
  );
}
